import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { applyItemNameFilter } from '../utils/customerItemNameFilter';
import { applyRouteFilter } from '../utils/customerRouteFilter';
import { Box, Typography, Card, CardContent, CircularProgress } from '@mui/material';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import * as XLSX from 'xlsx';

// 핸즈온테이블 스타일링 (정산 화면용으로 최적화)
const tableStyles = `
  .handsontable {
    font-size: 14px !important;
    font-family: 'Roboto', sans-serif !important;
    overflow: hidden !important;
  }
  
  .handsontable .wtHolder {
    overflow: auto !important;
    scrollbar-width: thin !important;
    scrollbar-color: #c1c1c1 #f1f1f1 !important;
  }
  
  .handsontable .wtHolder::-webkit-scrollbar {
    width: 12px !important;
    height: 12px !important;
  }
  
  .handsontable .wtHolder::-webkit-scrollbar-track {
    background: #f1f1f1 !important;
    border-radius: 6px !important;
  }
  
  .handsontable .wtHolder::-webkit-scrollbar-thumb {
    background: #c1c1c1 !important;
    border-radius: 6px !important;
    border: 2px solid #f1f1f1 !important;
  }
  
  .handsontable .wtHolder::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8 !important;
  }
  
  .handsontable .wtHolder::-webkit-scrollbar-corner {
    background: #f1f1f1 !important;
  }

  /* 왼쪽 행 헤더 스크롤 숨기기 */
  .handsontable .ht_clone_left .wtHolder {
    overflow: hidden !important;
  }
  
  .handsontable .ht_clone_left .wtHolder::-webkit-scrollbar {
    display: none !important;
  }
  
  /* 상단 헤더 스크롤 숨기기 */
  .handsontable .ht_clone_top .wtHolder {
    overflow: hidden !important;
  }
  
  .handsontable .ht_clone_top .wtHolder::-webkit-scrollbar {
    display: none !important;
  }
  
  /* 상단 왼쪽 코너 스크롤 숨기기 */
  .handsontable .ht_clone_top_left_corner .wtHolder {
    overflow: hidden !important;
  }
  
  .handsontable .ht_clone_top_left_corner .wtHolder::-webkit-scrollbar {
    display: none !important;
  }
  
  .handsontable .htCenter {
    text-align: center !important;
  }
  
  .handsontable .htMiddle {
    vertical-align: middle !important;
  }
  
  .handsontable .htRight {
    text-align: right !important;
  }
  
  .handsontable td {
    border: 1px solid #e0e0e0 !important;
  }
  
  .handsontable th {
    background-color: #f5f5f5 !important;
    border: 1px solid #e0e0e0 !important;
    font-weight: 600 !important;
  }
`;

interface CustomerInfoType {
  name: string;
  color: string;
  backgroundColor: string;
  lightColor: string;
}

interface Props {
  customerInfo: CustomerInfoType | null;
  isMobile: boolean;
  dateRange: { start: string; end: string };
  /** 배차 API `company` 쿼리 — CustomerPage의 currentCompany(거래처 표시명→배차 회사문자열). 있으면 하위 if-else 대신 사용 */
  apiCompanyName?: string;
  onTotalFeeChange?: (total: number) => void;
  onExcelRowsChange?: (rows: { headers: string[]; rows: any[][]; company?: string }) => void;
  hasQueried?: boolean;
  itemNameFilterType?: 'all' | 'allow' | 'block';
  itemNameList?: string[];
  routeFilterType?: 'all' | 'allow';
  routeList?: string[];
}

/** display name → dispatch row `company` (apiCompanyName이 없을 때만) */
function legacyCompanyNameFromCustomerName(customerInfo: CustomerInfoType | null): string {
  if (!customerInfo) return '';
  const n = customerInfo.name;
  if (n === '오뚜기라면') return '오뚜기농협물류';
  if (n === '오뚜기물류서비스(주)') return '오뚜기물류서비스(주)';
  if (n === '돌코리아') return '돌코리아';
  if (n === '츄고쿠삼화페인트') return 'csp';
  if (n === '(주)엘앤비') return '(주)엘앤비';
  if (n === '대륭기업(주)') return '대륭기업(주)';
  if (n === '용신물류') return '용신물류';
  if (n === '성양로지스틱스') return '성양로지스틱스';
  if (n === '푸르미영농조합') return '푸르미영농조합';
  if (n === '케이디케미칼') return '케이디케미칼';
  if (n === '동양제관') return '동양제관';
  if (n === '(주)낙토') return '(주)낙토';
  if (n === '(주)부광정공') return '(주)부광정공';
  if (n === '하나로농협물류') return '하나로농협물류';
  return '';
}

const CustomerSettlementViewer: React.FC<Props> = ({
  customerInfo,
  isMobile,
  dateRange,
  apiCompanyName,
  onTotalFeeChange,
  onExcelRowsChange,
  hasQueried = false,
  itemNameFilterType = 'all',
  itemNameList,
  routeFilterType = 'all',
  routeList,
}) => {
  const hotTableRef = useRef<any>(null);
  
  // 라면 관련 상태 (오뚜기라면 화주용)
  const [ramenRows, setRamenRows] = useState<any[]>([]);
  const [ramenLoading, setRamenLoading] = useState(false);
  
  // 정산 데이터 상태 및 로딩
  const [settlementRows, setSettlementRows] = useState<any[]>([]);
  const [settlementLoading, setSettlementLoading] = useState(false);

  // 오뚜기라면 화주인지 체크
  const isOtokiRamen = customerInfo?.name === '오뚜기라면';

  // 라면 데이터 조회 함수
  const handleRamenSearch = async () => {
    if (!dateRange.start || !dateRange.end) return;
    setRamenLoading(true);
    try {
      const res = await fetch(`/api/search-ramen?start=${dateRange.start}&end=${dateRange.end}`);
      if (!res.ok) throw new Error('데이터 없음');
      const ramenData = await res.json();
      setRamenRows(ramenData);
    } catch {
      setRamenRows([]);
    }
    setRamenLoading(false);
  };

  // 라면 엑셀 다운로드 함수
  const handleRamenExcel = () => {
    if (!ramenRows.length) return;
    // 합계 행까지 포함된 데이터로 변환
    const aoa = [ramenTableHeaders, ...getRamenTableData()];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 18 }, // 날짜
      { wch: 10 }, // 수출라면
      { wch: 10 }, // 내수라면
      { wch: 10 }, // 유지
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '라면');
    XLSX.writeFile(wb, `라면_${dateRange.start}_${dateRange.end}.xlsx`);
  };

  // 라면 합계 계산
  const ramenSum = ramenRows.reduce(
    (acc, r) => ({
      exportramenNum: acc.exportramenNum + (Number(r.exportramenNum) || 0),
      domesticramenNum: acc.domesticramenNum + (Number(r.domesticramenNum) || 0),
      oilramenNum: acc.oilramenNum + (Number(r.oilramenNum) || 0),
    }),
    { exportramenNum: 0, domesticramenNum: 0, oilramenNum: 0 }
  );

  // 오뚜기라면 화주일 때 자동으로 라면 데이터 조회
  useEffect(() => {
    if (isOtokiRamen && dateRange.start && dateRange.end) {
      handleRamenSearch();
    }
  }, [isOtokiRamen, dateRange]);

  // 실제 조회가 수행되었는지(없음 메시지 노출 제어용)
  const [didQuery, setDidQuery] = useState(false);

  const companyName = useMemo(() => {
    const trimmed = (apiCompanyName || '').trim();
    if (trimmed) return trimmed;
    return legacyCompanyNameFromCustomerName(customerInfo);
  }, [apiCompanyName, customerInfo]);

  // 정산 데이터 fetch 함수
  const fetchSettlementData = useCallback(async () => {
    const getDateList = (start: string, end: string) => {
      const result: string[] = [];
      let cur = new Date(start);
      const endDate = new Date(end);
      while (cur <= endDate) {
        result.push(cur.toISOString().slice(0, 10));
        cur.setDate(cur.getDate() + 1);
      }
      return result;
    };
    const dateList = getDateList(dateRange.start, dateRange.end);
    if (!customerInfo || dateList.length === 0) return;
    if (!companyName) {
      setSettlementLoading(true);
      setDidQuery(true);
      setSettlementRows([]);
      setSettlementLoading(false);
      return;
    }
    setSettlementLoading(true);
    setDidQuery(true);
    // 이전 데이터 즉시 클리어
    setSettlementRows([]);
    try {
      const allRows: any[] = [];
      for (const date of dateList) {
        const res = await fetch(`/api/dispatch-reports/${date}/rows?company=${encodeURIComponent(companyName)}`);
        const rows = await res.json();
        rows.forEach((row: any) => {
          row.date = date;
        });
        allRows.push(...rows);
      }
      allRows.sort((a, b) => a.date.localeCompare(b.date));
      setSettlementRows(allRows);
    } catch (error) {
      setSettlementRows([]);
    } finally {
      setSettlementLoading(false);
    }
  }, [customerInfo, dateRange, companyName]);

  // dateRange, customerInfo 변경 시 fetchSettlementData 실행
  useEffect(() => {
    fetchSettlementData();
  }, [fetchSettlementData]);

  // 금액 문자열(₩, 공백 등 포함) → 숫자 변환 함수
  const parseAmount = (val: any) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const num = Number(val.replace(/[^\d.-]/g, ''));
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  // 스타일 태그 추가
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = tableStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const isCsp = companyName === 'csp';
  const isOtokiLogistics = companyName === '오뚜기물류서비스(주)';
  const showItemNameColumn = isCsp || isOtokiLogistics;

  // 품명 → 운송구간(포함) 필터 (관리자 부서 설정)
  const filteredSettlementRows = useMemo(() => {
    const withRoute = (settlementRows || []).filter((row: any) => row.route || row.vehicleNumber);
    const byItem = applyItemNameFilter(withRoute, itemNameFilterType, itemNameList || []);
    return applyRouteFilter(byItem, routeFilterType || 'all', routeList || []);
  }, [settlementRows, itemNameFilterType, itemNameList, routeFilterType, routeList]);

  // 필터 적용된 정산 행의 billingAmount 합계 계산 및 상위로 전달
  useEffect(() => {
    if (onTotalFeeChange) {
      const total = filteredSettlementRows.reduce((sum, row) => sum + parseAmount(row.billingAmount), 0);
      onTotalFeeChange(total);
    }
  }, [filteredSettlementRows, onTotalFeeChange]);

  // 핸즈온테이블용 데이터 변환 (표 렌더링용, 헤더 없이, 번호 없이)
  const getTableData = () => {
    if (!filteredSettlementRows || filteredSettlementRows.length === 0) {
      return [];
    }
    if (showItemNameColumn) {
      return filteredSettlementRows.map((row: any) => [
        row?.date || '-',
        row?.route || '-',
        row?.vehicleNumber || '-',
        row?.cargo || '-',
        row?.itemName || '-',
        row?.billingAmount !== undefined && row?.billingAmount !== null && row?.billingAmount !== ''
          ? parseAmount(row.billingAmount) : null
      ]);
    } else {
      return filteredSettlementRows.map((row: any) => [
        row?.date || '-',
        row?.route || '-',
        row?.vehicleNumber || '-',
        row?.vehicleType || row?.cargo || '-',
        row?.billingAmount !== undefined && row?.billingAmount !== null && row?.billingAmount !== ''
          ? parseAmount(row.billingAmount) : null
      ]);
    }
  };

  // 핸즈온테이블 컬럼 헤더 분기 (useMemo로 감싸기)
  const tableHeaders = useMemo(() => (
    showItemNameColumn
      ? ['날짜', '운송구간', '차량번호', '차종', '품명', '운송료']
      : ['날짜', '운송구간', '차량번호', '차종', '운송료']
  ), [showItemNameColumn]);
  const tableColWidths = useMemo(() => (
    showItemNameColumn
      ? [120, 300, 120, 100, 120, 120]
      : [120, 300, 120, 100, 120]
  ), [showItemNameColumn]);

  // 핸즈온테이블 셀 스타일 설정
  const tableCells = (row: number, col: number) => {
    const cellProps: any = {
      readOnly: true
    };

    // 품명 컬럼이 있는 경우(츄고쿠삼화페인트, 오뚜기물류서비스(주)): 품명 컬럼은 4번째(인덱스 4), 운송료 컬럼은 5번째(인덱스 5)
    // 일반 거래처의 경우: 운송료 컬럼은 4번째(인덱스 4)
    const billingAmountColIndex = showItemNameColumn ? 5 : 4;

    // 운송료 컬럼 스타일 (오른쪽 정렬, 숫자 서식)
    if (col === billingAmountColIndex) {
      cellProps.className = 'htRight htMiddle';
      cellProps.style = { 
        backgroundColor: '#f8f9fa', 
        color: customerInfo?.color || '#1976d2', 
        fontWeight: 700 
      };
      // 숫자 서식 적용
      cellProps.numericFormat = { pattern: '#,##0' };
      cellProps.type = 'numeric';
    } else {
      cellProps.className = 'htCenter htMiddle';
    }

    return cellProps;
  };

  // 테이블 리사이즈 핸들러
  const handleResize = useCallback(() => {
    try {
      if (hotTableRef.current && hotTableRef.current.hotInstance) {
        setTimeout(() => {
          if (hotTableRef.current && hotTableRef.current.hotInstance) {
            hotTableRef.current.hotInstance.render();
          }
        }, 100);
      }
    } catch (error) {
      console.warn('핸즈온테이블 리사이즈 에러:', error);
    }
  }, []);

  // 윈도우 리사이즈 이벤트 리스너
  useEffect(() => {
    const resizeHandler = () => {
      setTimeout(handleResize, 100);
    };
    
    window.addEventListener('resize', resizeHandler);
    
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        setTimeout(handleResize, 300);
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: true });
    
    return () => {
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [handleResize]);

  // 데이터 변경 시 HotTable 업데이트
  useEffect(() => {
    if (hotTableRef.current && hotTableRef.current.hotInstance) {
      const hotInstance = hotTableRef.current.hotInstance;
      const newData = getTableData();
      
      // 데이터 로드
      hotInstance.loadData(newData);
      
      // 렌더링 강제 실행
      setTimeout(() => {
        if (hotTableRef.current && hotTableRef.current.hotInstance) {
          hotTableRef.current.hotInstance.render();
        }
      }, 100);
    }
  }, [filteredSettlementRows]);

  // settlementRows가 변경될 때마다 엑셀용 데이터(표 렌더링용) 상위로 전달
  useEffect(() => {
    if (typeof onExcelRowsChange === 'function') {
      onExcelRowsChange({
        headers: tableHeaders,
        rows: getTableData(), // 표 데이터 그대로 사용 (이미 숫자로 처리됨)
        company: companyName
      });
    }
  }, [filteredSettlementRows, showItemNameColumn, companyName]);

  // 라면 집계 Handsontable 데이터 변환 함수 추가
  const getRamenTableData = () => {
    const rows = ramenRows.map(r => [
      r.date,
      Number(r.exportramenNum || 0),
      Number(r.domesticramenNum || 0),
      Number(r.oilramenNum || 0),
    ]);
    // 합계 행 추가 (소수점 두 자리로 제한)
    rows.push([
      '합계',
      Number(ramenSum.exportramenNum.toFixed(2)),
      Number(ramenSum.domesticramenNum.toFixed(2)),
      Number(ramenSum.oilramenNum.toFixed(2)),
    ]);
    return rows;
  };
  const ramenTableHeaders = ['날짜', '수출라면', '내수라면', '유지'];
  const ramenTableColWidths = [120, 120, 120, 120];

  // 엑셀 다운로드 함수 (정산 표용, csp일 때만 품명 포함, 번호 포함)
  const handleSettlementExcel = () => {
    const tableData = getTableData(); // 표 데이터 그대로 사용 (이미 숫자로 처리됨)
    if (!tableData.length) return;
    // 표 헤더 그대로 사용
    const headers = tableHeaders;
    const aoa = [headers, ...tableData];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = tableColWidths.map(wch => ({ wch }));
    
    // 운송료 컬럼에 숫자 서식 적용
    const range = XLSX.utils.decode_range(ws['!ref'] || '');
    const billingAmountColIndex = showItemNameColumn ? 5 : 4; // 품명 컬럼이 있으면 5번째, 없으면 4번째 컬럼
    
    for (let r = 1; r <= range.e.r; r++) { // r=1부터: 헤더 제외
      const cellAddress = XLSX.utils.encode_cell({ c: billingAmountColIndex, r });
      const cell = ws[cellAddress];
      if (cell && cell.v !== null && cell.v !== '') {
        cell.z = '#,##0'; // 숫자 서식 적용
      }
    }
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '정산내역');
    XLSX.writeFile(wb, `정산내역_${dateRange.start}_${dateRange.end}.xlsx`);
  };

  // 필터 적용 후 표시할 데이터가 하나라도 있는지 여부
  const hasRouteData = filteredSettlementRows && filteredSettlementRows.length > 0;
  
  // 데이터 없음 메시지는 내부 fetch가 실제로 시작된 뒤(didQuery)이고,
  // 로딩이 끝났으며, 표시 가능한 데이터가 없을 때만 노출
  const shouldShowNoData = didQuery && !settlementLoading && !hasRouteData;

  return (
    <Box sx={{ 
      width: '100%', 
      px: { xs: 1, sm: 2 }, 
      pt: 2, 
      pb: 8, // 하단 여유 공간을 더 확보
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      // 모바일에서는 기본 흰색 배경 유지
    }}>
      {isMobile ? (
        /* 모바일: 안내 메시지만 표시 */
        <Box sx={{ 
          textAlign: 'center', 
          color: '#666', 
          mt: 4,
          mx: 2, // 좌우 여백 추가
          p: 3,
          bgcolor: '#f9f9f9',
          borderRadius: 2,
          maxWidth: 'calc(100% - 16px)', // 최대 너비 제한
          boxSizing: 'border-box' // 패딩을 포함한 크기 계산
        }}>
          <Typography 
            color="text.secondary" 
            align="center" 
            sx={{ 
              fontSize: isMobile ? 16 : 18,
              fontWeight: 600,
              lineHeight: 1.4,
              wordBreak: 'keep-all'
            }}
          >
            정산 미리보기 자료는 PC에서 확인 가능합니다
          </Typography>
        </Box>
      ) : settlementLoading ? (
        <Box sx={{ 
          width: '100%', 
          height: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <CircularProgress sx={{ color: '#1976d2' }} />
        </Box>
      ) : isOtokiRamen ? (
        /* 오뚜기라면 화주: 라면 테이블을 Handsontable로 표시 */
        <Box sx={{ width: '100%', maxWidth: '800px', mx: 'auto' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            p: 2,
            bgcolor: '#fff9c4',
            borderRadius: 2,
            border: '1px solid #ffc107'
          }}>
            <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 700 }}>
              라면 집계 ({dateRange.start} ~ {dateRange.end})
            </Typography>
            {ramenRows.length > 0 && (
              <button
                onClick={handleRamenExcel}
                style={{
                  background: '#43a047',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 16px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                엑셀 다운로드
              </button>
            )}
          </Box>
          {ramenLoading ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              color: '#e65100',
              fontWeight: 700,
              fontSize: 18
            }}>
              <CircularProgress size={20} sx={{ color: '#1976d2' }} />
            </Box>
          ) : ramenRows.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              color: '#666', 
              mt: 4,
              p: 3,
              bgcolor: '#f9f9f9',
              borderRadius: 2
            }}>
              <Typography variant="body1">
                선택한 기간에 라면 데이터가 없습니다.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: '#fff',
              px: 0,
              py: 2
            }}>
              <HotTable
                data={getRamenTableData()}
                colHeaders={ramenTableHeaders}
                colWidths={ramenTableColWidths}
                rowHeaders={true}
                width="100%"
                height={Math.min(400, 48 * (ramenRows.length + 2))}
                licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
                stretchH="all"
                readOnly={true}
                cells={(row, col) => {
                  // 합계 행 스타일
                  if (row === ramenRows.length) {
                    return {
                      className: col === 0 ? 'htCenter htMiddle' : 'htRight htMiddle',
                      style: { backgroundColor: '#fffde7', fontWeight: 700, color: '#e65100', fontSize: '16px' }
                    };
                  }
                  // 기본 스타일
                  return {
                    className: col === 0 ? 'htCenter htMiddle' : 'htRight htMiddle',
                    style: { color: '#1976d2', fontWeight: 600 }
                  };
                }}
                contextMenu={false}
                preventOverflow="horizontal"
                outsideClickDeselects={false}
                fillHandle={false}
                disableVisualSelection={true}
              />
            </Box>
          )}
        </Box>
      ) : shouldShowNoData ? (
        <Box sx={{ 
          width: '100%',
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Card sx={{
            bgcolor: '#f8fafb',
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            p: 2,
            width: '100%',
            maxWidth: '500px',
            minHeight: '80px',
            boxSizing: 'border-box',
            border: '1px solid #f0f0f0',
          }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography
                color="text.secondary"
                align="center"
                sx={{ 
                  fontSize: 18,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  wordBreak: 'keep-all'
                }}
              >
                해당 날짜에 정산 내역이 존재하지 않습니다.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      ) : (
        /* 일반 화주: 핸즈온테이블 표시 */
        <Box sx={{ 
          width: '95%',
          height: '600px', // 배차 표와 동일하게 고정 높이
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#fff',
          mx: 'auto',
        }}>
          <HotTable
            ref={hotTableRef}
            data={getTableData()}
            colHeaders={tableHeaders}
            colWidths={tableColWidths}
            rowHeaders={true}
            width="100%"
            height="580px"
            licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
            stretchH="all"
            fixedRowsTop={0}
            fixedColumnsLeft={0}
            manualRowResize={false}
            manualColumnResize={true}
            autoColumnSize={false}
            autoRowSize={false}
            renderAllRows={true}
            viewportRowRenderingOffset={100}
            className="htCenter"
            cells={tableCells}
            contextMenu={false}
            readOnly={true}
            preventOverflow="horizontal"
            outsideClickDeselects={false}
            fillHandle={false}
            disableVisualSelection={true}
            columns={showItemNameColumn ? [
              { data: 0, type: 'text' }, // 날짜
              { data: 1, type: 'text' }, // 운송구간
              { data: 2, type: 'text' }, // 차량번호
              { data: 3, type: 'text' }, // 차종
              { data: 4, type: 'text' }, // 품명
              { data: 5, type: 'numeric', numericFormat: { pattern: '#,##0' } } // 운송료
            ] : [
              { data: 0, type: 'text' }, // 날짜
              { data: 1, type: 'text' }, // 운송구간
              { data: 2, type: 'text' }, // 차량번호
              { data: 3, type: 'text' }, // 차종
              { data: 4, type: 'numeric', numericFormat: { pattern: '#,##0' } } // 운송료
            ]}
            afterInit={() => {
              // HotTable 초기화 완료
            }}
            afterLoadData={() => {
              // HotTable 데이터 로드 완료
            }}
            afterRender={() => {
              // HotTable 렌더링 완료
            }}
          />
          <div style={{ height: 32 }} />
        </Box>
      )}
    </Box>
  );
};

export default CustomerSettlementViewer; 